/**
 * The reporting calendar: what calendar day it is, and when a calendar day ends, in the zone the
 * business reports in.
 *
 * The zone is a constant today and will become a firm column later — a fund administrator
 * operating out of London closes its quarters on London days, so `reportingDateOf` /
 * `reportingEndOfDay` will gain a zone argument and `REPORTING_TIME_ZONE` will become the default
 * for a firm that has not set one. Hardcoding Eastern is right for the initial release because
 * every current firm is US-based, and a per-firm zone would put a database read in front of
 * `today()`, which write paths call with no firm context.
 *
 * The zone applies only where an instant is assigned to a calendar day — "what is today" and the
 * transaction-time bound on the temporal tables (AC-16/17). Every event-date column the rewind
 * bounds is a Postgres `date`, so comparing two of them never involves a timezone. The zone also
 * does not apply to display formatting: a stored `YYYY-MM-DD` is parsed and rendered with
 * `timeZone: 'UTC'` (`src/utils/format.ts`), which is correct — it is a label, not an instant.
 */

/** The zone the business reports in (AC-31). */
export const REPORTING_TIME_ZONE = 'America/New_York';

// Constructed once: `today()` runs in `createContext` on every request and in every frontend
// preset and `max` computation, so a formatter built per call would be the slow path of this API.
const DATE_PARTS_FORMATTER = new Intl.DateTimeFormat('en-US', {
  timeZone: REPORTING_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const OFFSET_FORMATTER = new Intl.DateTimeFormat('en-US', {
  timeZone: REPORTING_TIME_ZONE,
  timeZoneName: 'longOffset',
});

/**
 * The `YYYY-MM-DD` calendar date, in the reporting zone, on which an instant falls.
 *
 * @param instant - The instant to place on the reporting calendar
 * @returns The reporting-zone calendar date
 */
export function reportingDateOf(instant: Date): string {
  const parts = DATE_PARTS_FORMATTER.formatToParts(instant);
  const year = parts.find((p) => p.type === 'year')!.value;
  const month = parts.find((p) => p.type === 'month')!.value;
  const day = parts.find((p) => p.type === 'day')!.value;

  return `${year}-${month}-${day}`;
}

/**
 * The reporting zone's UTC offset, in milliseconds, at the given instant.
 *
 * Exported so tests can pin DST transition dates directly.
 *
 * @param instant - The instant to read the offset at
 * @returns The offset in milliseconds, positive east of UTC
 */
export function reportingOffsetMs(instant: Date): number {
  const offsetPart = OFFSET_FORMATTER.formatToParts(instant).find((p) => p.type === 'timeZoneName')!.value;
  const match = /GMT([+-])(\d{2}):(\d{2})/.exec(offsetPart);
  if (!match)
    throw new Error(`Unexpected timezone offset format: ${offsetPart}`);

  const sign = match[1] === '-' ? -1 : 1;
  const hours = Number(match[2]);
  const minutes = Number(match[3]);

  return sign * (hours * 60 + minutes) * 60 * 1000;
}

/**
 * The instant at which the reporting-zone calendar date `dateStr` ends (23:59:59.999 local).
 *
 * One offset lookup is enough: the naive UTC instant for `dateStr 23:59:59.999` is `19:59:59.999`
 * Eastern on the same calendar day, and `America/New_York` transitions at 02:00 local, so the
 * naive instant and the true answer fall on the same side of any DST transition and share an
 * offset.
 *
 * @param dateStr - A reporting-zone calendar date, in YYYY-MM-DD
 * @returns The instant that date ends, in the reporting zone
 */
export function reportingEndOfDay(dateStr: string): Date {
  const naive = Date.parse(`${dateStr}T23:59:59.999Z`);
  const offsetMs = reportingOffsetMs(new Date(naive));

  return new Date(naive - offsetMs);
}
